import React, { useState } from 'react';

import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { v4 as uuid } from 'uuid';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import { post } from 'helpers/api';
import { refreshActivities } from 'bundles/apex/activities/hooks';
import { showSnackbar } from 'components/ui/snackbar/helper';

import SendEmailForm from './form';
import { buildRequestBody, errorMessage, getFileSize, MAX_ATTACHMENT_BYTES, splitEmails } from './helpers';

import type { ApiError, EmailFile, EmailFileReference, EmailFormState, SentEmail } from './types';

interface Props {
  initialAttachments?: EmailFileReference[];
  initialBody?: string;
  initialSubject?: string;
  path: string;
  to: string[];
  onCancel?: () => void;
  onClose: () => void; // Fires on submit and on cancel - use other callbacks to distinguish if needed
  onSend?: (sent: SentEmail) => void ;
}

// Unlike most modals which are always mounted and managed with an `open` prop, this modal must be
// remounted on show as Trix reads its input once on init.
export default function SendEmailModal({
  initialAttachments, initialBody, initialSubject, path, to, onCancel, onClose, onSend
}: Props) {
  const [form, setForm] = useState<EmailFormState>({
    to: to.join(', '),
    cc: '',
    bcc: '',
    subject: initialSubject || '',
    body: initialBody || '',
  });
  const [files, setFiles] = useState<EmailFile[]>(initialAttachments || []);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => uuid());

  const tooLarge = files.reduce((sum, file) => sum + getFileSize(file), 0) > MAX_ATTACHMENT_BYTES;
  const canSubmit = splitEmails(form.to).length > 0 && !tooLarge;

  function handleCancel() {
    onCancel?.();
    onClose();
  }

  const updateForm = (changes: Partial<EmailFormState>) => setForm(prev => ({ ...prev, ...changes }));
  const addFiles = (added: File[]) => setFiles(prev => [...prev, ...added]);
  const removeFile = (index: number) => setFiles(prev => prev.filter((_, i) => i !== index));

  async function handleSend() {
    setError(null);
    setSending(true);

    try {
      const payload = {
        to: splitEmails(form.to),
        cc: splitEmails(form.cc),
        bcc: splitEmails(form.bcc),
        subject: form.subject,
        body: form.body,
      };
      const sentEmail: SentEmail = await post(path, buildRequestBody(payload, files, idempotencyKey));

      refreshActivities();
      showSnackbar({ text: 'Email sent', variant: 'success', icon: faEnvelope });

      onSend?.(sentEmail);
      onClose();
    } catch (err) {
      setError(errorMessage(err as ApiError));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal open onClose={handleCancel}>
      <Modal.Content size="lg">
        <Modal.Header>
          <Modal.Title className="d-flex gap-2 align-items-center">
            <FontAwesomeIcon className="text-primary" icon={faEnvelope} />
            Send Email
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert className="mb-3" severity="destructive">{error}</Alert>}
          {tooLarge && <Alert className="mb-3" severity="warning">Attachments exceed the 25 MB limit.</Alert>}

          <SendEmailForm
            files={files}
            form={form}
            showBcc={showBcc}
            showCc={showCc}
            onAddFiles={addFiles}
            onChange={updateForm}
            onRemoveFile={removeFile}
            onToggleBcc={() => setShowBcc(true)}
            onToggleCc={() => setShowCc(true)}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button disabled={sending} label="Cancel" variant="ghost" onClick={handleCancel} />
          <Button disabled={sending || !canSubmit} onClick={handleSend}>
            {sending && <LoadingSpinner flex size="md" skipTopBuffer />}
            {sending ? 'Sending…' : 'Send Email'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
